import React, { useCallback, useState } from "react";
import { FlaskConical } from "lucide-react";
import Layout from "src/components/app/layout/Layout";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  searchLabTestsRequest,
  cartListDeleteRequest,
} from "src/redux/slices/labTestSlice";
import { LabTestListData, SearchLabTestResult } from "src/types/labTests.types";
import GenericSearchScreen from "src/components/common/Search/GenericSearchScreen";
import Modal from "src/components/common/Modal";
import {
  testItemToShow,
  TestItem,
} from "src/components/app/lab-tests/CategoryTestListView";
import { useAddToCart } from "src/hooks/useAddLabTestToCart";
import { useLabTestCart } from "src/helpers/common";
import Loader from "src/components/common/Loader";

const recentSearches: any[] = [
  //{ name: "ESR Test", category: "Recommended tests for you" },
  //{ name: "Kidney Function Test (KFT)", category: "Recommended tests for you" },
];

type ShowSearchedScreenProps = {
  searchedLabTest: TestItem;
  closeModal: () => void;
};

const ShowSearchedScreen: React.FC<ShowSearchedScreenProps> = ({
  searchedLabTest,
  closeModal,
}) => {
  const dispatch = useAppDispatch();
  const testsAddedToCart = useAppSelector(
    (state) => state.labTest.cartState.data?.tests_list
  );
  const { fetchLabTestCart } = useLabTestCart();
  const testIdsInCart: { lab_test_name: string; code?: string }[] =
    testsAddedToCart?.map((test) => {
      return {
        lab_test_name: test.name,
        code: test.code,
      };
    }) || [];
  const { handleAddToCart, isTestLoading, isAnyTestLoading } =
    useAddToCart(fetchLabTestCart);

  const [removingTestCode, setRemovingTestCode] = useState<string | null>(null);

  const handleRemoveFromCart = (code: string) => {
    setRemovingTestCode(code);
    new Promise((resolve, reject) => {
      dispatch(
        cartListDeleteRequest({
          resolve,
          reject,
          payload: { code },
        })
      );
    })
      .then(() => {
        fetchLabTestCart();
      })
      .catch((err) => {
        console.error("Error removing from cart:", err);
      })
      .finally(() => {
        setRemovingTestCode(null);
      });
  };

  const isRemovingTest = (code: string) => removingTestCode === code;

  return (
    <Modal
      show={true}
      showCloseButton={true}
      handleClose={closeModal}
      fullScreen={true}
      title="Test Details"
    >
      {testItemToShow(
        searchedLabTest,
        0,
        testIdsInCart,
        handleAddToCart,
        true,
        isTestLoading,
        isAnyTestLoading,
        handleRemoveFromCart,
        isRemovingTest,
        true
      )}
    </Modal>
  );
};

const SearchListView: React.FC<{
  item: SearchLabTestResult;
  index: number;
  onItemClick: (item: TestItem) => void;
  supportingItems: LabTestListData[];
}> = ({ item, index, onItemClick, supportingItems }) => {
  return (
    <div
      key={index}
      className="flex items-start space-x-3"
      onClick={() => {
        const foundCategoryItem = supportingItems.find(
          (categoryItem) => categoryItem.category_name === item.category_name
        );
        const foundTestItem = foundCategoryItem?.catalogs_list.find(
          (testItem) => testItem.catalog_name === item.catalog_name
        );
        if (foundTestItem) {
          onItemClick(foundTestItem as TestItem);
        } else {
          const fallbackTestItem: TestItem = {
            catalog_name: item.catalog_name,
            description: `Lab test ${item.catalog_name} (Check pricing in cart details)`,
            mrp_price: undefined,
            selling_price: undefined,
            b2c_price: undefined,
            diagnostic_catalog_master_id: item.diagnostic_catalog_master_id,
          };
          onItemClick(fallbackTestItem);
        }
      }}
    >
      {/* Icon */}
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <FlaskConical className="w-5 h-5 text-gray-700" />
      </div>

      {/* Text */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {item.catalog_name}
        </p>
        <p className="text-xs text-gray-500 truncate">{item.category_name}</p>
      </div>
    </div>
  );
};

const LabTestSearchScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const labTestList = useAppSelector((state) => state.labTest.labTests.data);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchedLabTest, setSearchedLabTest] = useState<TestItem | null>(null);

  const openModal = (item: TestItem) => {
    setShowModal(true);
    setSearchedLabTest(item);
  };

  const closeModal = () => {
    setShowModal(false);
    setSearchedLabTest(null);
  };

  const fetchResults = useCallback(
    (searchTerm: string, page: number) =>
      new Promise((resolve, reject) => {
        dispatch(
          searchLabTestsRequest({
            resolve,
            reject,
            payload: {
              page: String(page),
              search: searchTerm,
              // support_program_type: getSupportProgramType(
              //   supportProgramKey || ""
              // ),
            },
          })
        );
      }).then((res: any) => {
        const items = res?.data || [];
        return {
          items,
          hasMore: items.length === 20,
        };
      }),
    [dispatch]
  );

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <GenericSearchScreen<SearchLabTestResult, LabTestListData>
        title="Search Lab Tests"
        placeholder="Search for lab test"
        fetchResults={fetchResults}
        renderItem={(item, index) => (
          <SearchListView
            key={index}
            item={item}
            index={index}
            onItemClick={openModal}
            supportingItems={labTestList || []}
          />
        )}
        //showSearchedScreen={showSearchedScreen}
      />
      {showModal && searchedLabTest && (
        <ShowSearchedScreen
          searchedLabTest={searchedLabTest}
          closeModal={closeModal}
        />
      )}
    </Layout>
  );
};

export default LabTestSearchScreen;
